<script>
import PatchForm from './PatchForm.vue';

import { nextTick } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { OpenApWarning, createEffect } from '@midulate/shared';

export default {
  name: "Patches",

  components: {
    PatchForm,
    OpenApWarning,
  },

  inject: ['midiMonitor'],

  emits: ['alert', 'deviceError', 'updateEffect', 'update:currentPatch'],

  methods: {
    async addPatch() {
      this.config.patches.push({
        title: `Patch ${this.config.patches.length + 1}`,
        uuid: uuidv4(),
        effects: [],
      });
      const index = this.config.patches.length - 1;

      if (this.connectedDevice) {
        try {
          await this.connectedDevice.uploadConfig(this.config);
        } catch (e) {
          this.$emit('deviceError', e, this.connectedDevice);
          return;
        }
      }

      await this.setCurrentPatch(index);
    },

    // Dismiss the mega-menu patch panel (native Popover API). Guarded so it is
    // a no-op in environments without popover support (e.g. jsdom under Vitest).
    closePatchPanel() {
      document.getElementById('patch-panel')?.hidePopover?.();
    },

    selectAndClose(index) {
      this.closePatchPanel();
      return this.setCurrentPatch(index);
    },

    // Reorder keeps the active patch selected — only its index shifts. The
    // patch's uuid is unchanged, so there is no device switch, and we leave
    // the mega-menu panel open so the user can keep dragging.
    onReorderSelect(index) {
      this.$emit('update:currentPatch', index);
    },

    addAndClose() {
      this.closePatchPanel();
      return this.addPatch();
    },

    async setCurrentPatch(index) {
      this.$emit('update:currentPatch', index);
      this.midiMonitor.clearAllStreams(1000);

      if (this.connectedDevice) {
        try {
          await this.connectedDevice.setPatch(this.config.patches[index].uuid);
        } catch (e) {
          this.$emit('deviceError', e, this.connectedDevice);
        }
      }
    },

    deletePatch(index) {
      this.config.patches.splice(index, 1);
      if (this.currentPatch >= this.config.patches.length) {
        this.$emit('update:currentPatch', this.config.patches.length - 1);
      }
      this.$emit('alert', 'success', 'Patch deleted');
    },

    // Every param the type understands, at the firmware's own defaults, so
    // the knobs and the device agree from the start. channel and cc stay
    // unset: Effect.vue sends nothing until both are chosen, which keeps an
    // unrouted effect silent.
    addEffect(patchIndex) {
      this.config.patches[patchIndex].effects.push(createEffect());
    },

    // Deleting has to reach the device, not just the config. The firmware
    // addresses effects by position (set_effect(index, …)), so while the device
    // still holds the old list two things are wrong: the deleted effect keeps
    // running, and every editor index after it is off by one, so the next edit
    // to a surviving effect would be applied to its neighbour on the device.
    // SET_EFFECTS replaces the list in one go and fixes both — same approach as
    // reorderEffects, for the same reason.
    async deleteEffect(patchIndex, effectIndex) {
      this.config.patches[patchIndex].effects.splice(effectIndex, 1);

      if (!this.connectedDevice || patchIndex !== this.currentPatch) {
        this.$emit('alert', 'success', 'Effect deleted');
        return;
      }

      // Let the Effect watchers enqueue their debounced UPDATE_EFFECT calls
      // before we cancel them — see reorderEffects for why nextTick is needed.
      // Those calls carry pre-delete indices, so they must not survive.
      await nextTick();
      this.connectedDevice.updateEffect.cancel();

      try {
        await this.connectedDevice.setEffects(this.config.patches[patchIndex].effects);
      } catch (e) {
        this.$emit('deviceError', e, this.connectedDevice);
        return;
      }

      this.$emit('alert', 'success', 'Effect deleted');
    },

    updateEffect(effectArgs) {
      this.$emit('updateEffect', effectArgs);
    },

    updatePatch(patchIndex, newPatch) {
      Object.assign(this.config.patches[patchIndex], newPatch);

      if (this.connectedDevice && patchIndex === this.currentPatch) {
        const { bpm, meter, swing, clock_source } = newPatch;
        this.connectedDevice.updatePatchGlobals({ bpm, meter, swing, clock_source });
      }
    },

    async reorderEffects(patchIndex, effects) {
      this.config.patches[patchIndex].effects = effects;

      if (!this.connectedDevice || patchIndex !== this.currentPatch) return;

      // Wait for Vue to re-render so that Effect component deep-watchers have
      // a chance to enqueue their debounced UPDATE_EFFECT calls BEFORE we cancel.
      // Without nextTick the cancel() runs before the watchers fire (Vue flushes
      // in the next microtask), so the spurious UPDATE_EFFECT calls would slip through.
      await nextTick();

      // Cancel any pending debounced UPDATE_EFFECT calls from the Effect watchers,
      // then send all effects in a single SET_EFFECTS command so the device
      // replaces its live effects array atomically and only resets the period once.
      this.connectedDevice.updateEffect.cancel();
      try {
        await this.connectedDevice.setEffects(effects);
      } catch (e) {
        this.$emit('deviceError', e, this.connectedDevice);
      }
    },
  },

  props: {
    capabilities: {
      type: Object,
      required: true,
    },

    config: {
      type: Object,
      required: true
    },

    connectedDevice: {
      type: Object,
      required: false,
      default: null
    },

    connectedToDevice: {
      type: Boolean,
      required: true,
      default: false
    },

    // Owned by App.vue so the selection survives navigating away from this
    // route — the component is unmounted and remounted on every tab change.
    currentPatch: {
      type: Number,
      required: true
    },

    fileOpened: {
      type: Boolean,
      required: true
    },

    tempoInfo: {
      type: Object,
      required: false,
      default: null
    }
  }
}
</script>
<template>
<div>
  <div v-if="fileOpened" class="px-4 pt-2">

    <!-- deviceKey scopes the dismissal so putting the warning away for one
         device does not silence it for another. USB connections have no
         baseUrl, which falls back to a shared key — acceptable, since the
         alternative is no warning memory at all (MDL-393). -->
    <OpenApWarning :settings="config.settings" :device-key="connectedDevice?.baseUrl ?? null" />

    <!-- Patch form — full width. The patch title doubles as the mega-menu
         switcher; Add/Delete patch live in its header. -->
    <PatchForm
      :patch="config.patches[currentPatch]"
      :patches="config.patches"
      :devices="config.settings.devices"
      :index="currentPatch"
      :connectedDevice="connectedDevice"
      :connectedToDevice="connectedToDevice"
      :tempoInfo="tempoInfo"
      :capabilities="capabilities"
      @selectPatch="selectAndClose"
      @reorderSelect="onReorderSelect"
      @addPatch="addAndClose"
      @deletePatch="deletePatch"
      @addEffect="addEffect"
      @deleteEffect="deleteEffect"
      @updateEffect="updateEffect"
      @updatePatch="updatePatch"
      @reorderEffects="reorderEffects"
    />

  </div>
</div>
</template>
